import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import {
  LocateFixed, Compass, Volume2, VolumeX, CornerUpLeft, CornerUpRight, ArrowUp, MoveUpLeft, MoveUpRight,
  Undo2, Redo2, Flag, AlertTriangle, Play, Pause, Loader2, ChevronRight, FlaskConical, BookOpen, Store, HelpCircle, Sparkles,
} from "lucide-react";
import { nearestPathIndex } from "@/lib/access";
import {
  distM, bearing, destPoint, angleDiff, projectOnPath, buildManeuvers, buildOwnerManeuvers, currentInstruction, fmtM, etaText, speak, setVoiceMuted,
} from "@/lib/nav";
import { LostButton } from "@/components/LostButton";

const NAVY = "#001A4D", BLUE = "#2563EB";
const ARRIVE_M = 40, OFF_ROUTE_M = 50;
const SHOP_NAME = "Whangamatā Cycles";

const TurnIcon = ({ type, className = "w-10 h-10" }) => {
  const map = {
    left: CornerUpLeft, right: CornerUpRight, "slight-left": MoveUpLeft, "slight-right": MoveUpRight,
    "sharp-left": Undo2, "sharp-right": Redo2, arrive: Flag, straight: ArrowUp,
  };
  const I = map[type] || ArrowUp;
  return <I className={className} strokeWidth={2.5} />;
};

// Full-screen, in-app turn-by-turn navigation. Every route starts and ends at the bike shop
// (first/last point of the KML path), so legs are: shop → stop 1 → … → last stop → shop.
export const NavigationScreen = ({ route, active, setActive, onExit, onFinish, onDistance, onPlayStory, playing, audioLoading, storyDoneId }) => {
  const stops = route.stops;
  const path = route.path?.length > 1 ? route.path : stops.map((s) => [s.lat, s.lng]);
  const shopStart = useMemo(() => ({ name: SHOP_NAME, lat: path[0][0], lng: path[0][1], shop: true }), [path]);
  const shopEnd = useMemo(() => ({ name: SHOP_NAME, lat: path[path.length - 1][0], lng: path[path.length - 1][1], shop: true }), [path]);
  const points = useMemo(() => [shopStart, ...stops, shopEnd], [shopStart, stops, shopEnd]);

  // legIndex j: points[j] → points[j+1]. Opening at story stop `active`: 0 → leave shop; else continue from that stop.
  const [legIndex, setLegIndex] = useState(active === 0 ? 0 : active + 1);
  const [pos, setPos] = useState(null);
  const [heading, setHeading] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [follow, setFollow] = useState(true);
  const [headingUp, setHeadingUp] = useState(true);
  const [muted, setMuted] = useState(false);
  const [arrivedAt, setArrivedAt] = useState(null); // points index we just arrived at
  const [sim, setSim] = useState(false);
  const [gpsError, setGpsError] = useState(null);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const userRef = useRef(null);
  const legLineRef = useRef(null);
  const offRouteLineRef = useRef(null);
  const markersRef = useRef([]);
  const lastPosRef = useRef(null);
  const spokenRef = useRef("");
  const simRef = useRef(null);

  const from = points[legIndex];
  const to = points[legIndex + 1];
  const finished = !to;

  const leg = useMemo(() => {
    if (!to) return [];
    let i = legIndex === 0 ? 0 : nearestPathIndex(path, from.lat, from.lng);
    let j = legIndex === points.length - 2 ? path.length - 1 : nearestPathIndex(path, to.lat, to.lng);
    if (i > j) [i, j] = [j, i];
    const slice = path.slice(i, j + 1);
    return slice.length > 1 ? slice : [[from.lat, from.lng], [to.lat, to.lng]];
  }, [path, from, to, legIndex, points.length]);
  const ownerNav = route.nav_mode === "owner";
  const legCues = legIndex === 0 ? route.start_cues || [] : from.cues || [];
  const maneuvers = useMemo(() => {
    const name = to?.shop ? `${SHOP_NAME} — ride's end` : to?.name || "";
    return ownerNav ? buildOwnerManeuvers(leg, legCues, name) : buildManeuvers(leg, name);
  }, [leg, to, ownerNav, legCues]);

  const onFix = useCallback((lat, lng, hdg, spd) => {
    const p = [lat, lng];
    const prev = lastPosRef.current;
    let h = hdg;
    if ((h == null || isNaN(h)) && prev && distM(prev, p) > 3) h = bearing(prev, p);
    if (prev) { const d = distM(prev, p); if (d > 2 && d < 200 && onDistance) onDistance(d); }
    lastPosRef.current = p;
    setPos(p);
    if (h != null && !isNaN(h)) setHeading(h);
    setSpeed(spd || 0);
  }, [onDistance]);

  // Keep the screen awake while navigating (supported mobile browsers)
  useEffect(() => {
    let lock = null;
    const acquire = async () => { try { if (navigator.wakeLock && document.visibilityState === "visible") lock = await navigator.wakeLock.request("screen"); } catch { /* not allowed */ } };
    acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => { document.removeEventListener("visibilitychange", acquire); try { lock?.release(); } catch { /* ignore */ } };
  }, []);

  useEffect(() => {
    if (sim) return;
    if (!navigator.geolocation) { setGpsError("Location isn't available on this device."); return; }
    const id = navigator.geolocation.watchPosition(
      (g) => { setGpsError(null); onFix(g.coords.latitude, g.coords.longitude, g.coords.heading, g.coords.speed); },
      () => setGpsError("Can't get your location. Allow location access in your browser settings."),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [sim, onFix]);

  // Preview ride: glide along the leg at ~15 km/h
  useEffect(() => {
    if (!sim) { if (simRef.current) clearInterval(simRef.current); return; }
    let i = 0, t = 0;
    const pts = leg.length > 1 ? leg : [[from.lat, from.lng]];
    simRef.current = setInterval(() => {
      if (i >= pts.length - 1) { clearInterval(simRef.current); return; }
      const segLen = distM(pts[i], pts[i + 1]) || 1;
      t += 8 / segLen;
      while (t >= 1 && i < pts.length - 1) { t -= 1; i += 1; }
      const a = pts[Math.min(i, pts.length - 1)], b = pts[Math.min(i + 1, pts.length - 1)];
      const k = Math.min(t, 1);
      onFix(a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, distM(a, b) > 0 ? bearing(a, b) : null, 4.2);
    }, 500);
    return () => clearInterval(simRef.current);
  }, [sim, leg, from, onFix]);

  useEffect(() => { spokenRef.current = ""; }, [legIndex]);
  useEffect(() => { setVoiceMuted(muted); }, [muted]);

  const proj = useMemo(() => (pos && leg.length > 1 ? projectOnPath(leg, pos) : null), [pos, leg]);
  const offRoute = proj ? proj.distM > OFF_ROUTE_M : false;
  const instr = useMemo(() => (proj ? currentInstruction(maneuvers, proj.alongM) : null), [proj, maneuvers]);
  const remainingM = proj && to ? Math.max(0, maneuvers[maneuvers.length - 1].atM - proj.alongM) : null;
  const distToNext = pos && to ? distM(pos, [to.lat, to.lng]) : null;

  // Arrival
  useEffect(() => {
    if (!pos || !to || arrivedAt != null) return;
    if (distToNext != null && distToNext < ARRIVE_M) {
      setSim(false);
      const idx = legIndex + 1;
      if (to.shop) { speak("You're back at Whangamatā Cycles. Ride complete!"); toast.success("Back at Whangamatā Cycles — ride complete"); onFinish(); return; }
      setArrivedAt(idx);
      setActive(idx - 1);
      speak(`You've arrived at ${to.name}. Tap play to hear the story.`);
      toast.success(`Arrived at ${to.name}`);
    }
  }, [pos, to, distToNext, arrivedAt, legIndex, setActive, onFinish]);

  const continueRide = useCallback(() => { setArrivedAt(null); setCountdown(null); setLegIndex((j) => j + 1); }, []);

  // Auto-continue: once the story has finished playing, count down and roll on to the next leg;
  // or if the rider simply rides off (>80m from the stop), continue straight away.
  const [countdown, setCountdown] = useState(null);
  const arrivedPoint = arrivedAt != null ? points[arrivedAt] : null;
  useEffect(() => {
    if (!arrivedPoint || playing) { setCountdown(null); return; }
    if (storyDoneId && storyDoneId === arrivedPoint.id) setCountdown(8);
  }, [arrivedPoint, storyDoneId, playing]);
  useEffect(() => {
    if (countdown == null) return;
    if (countdown <= 0) { speak(`Let's keep riding. Next up, ${points[arrivedAt + 1]?.shop ? "back to Whangamatā Cycles" : points[arrivedAt + 1]?.name}.`); continueRide(); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, continueRide, points, arrivedAt]);
  useEffect(() => {
    if (!arrivedPoint || !pos || playing) return;
    if (distM(pos, [arrivedPoint.lat, arrivedPoint.lng]) > 80) continueRide();
  }, [pos, arrivedPoint, playing, continueRide]);

  // Spoken prompts
  const [lastNote, setLastNote] = useState(null);
  const cueAudioRef = useRef(null);
  useEffect(() => { setLastNote(null); }, [legIndex]);
  const sayCue = useCallback((m) => {
    if (m.audio && !muted) {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      if (!cueAudioRef.current) cueAudioRef.current = new Audio();
      cueAudioRef.current.src = `${process.env.REACT_APP_BACKEND_URL}${m.audio}`;
      cueAudioRef.current.play().catch(() => speak(m.label));
    } else speak(m.label);
  }, [muted]);
  useEffect(() => () => { try { cueAudioRef.current?.pause(); } catch { /* ignore */ } }, []);
  useEffect(() => {
    if (!instr || offRoute || arrivedAt != null) return;
    const d = instr.distM;
    let key = null, text = null;
    if (ownerNav && instr.owner) {
      // Owner-plotted cue: read it word for word once the rider reaches it (≈35 m), no generated phrasing
      if (d < 35 || (pos && distM(pos, instr.point) < 35)) {
        key = `${legIndex}-${instr.atM}-owner`;
        if (instr.kind !== "dir") setLastNote(instr);
        if (spokenRef.current !== key) { spokenRef.current = key; sayCue(instr); }
        return;
      }
    } else if (instr.type === "arrive") {
      if (d < 120) { key = `${legIndex}-arrive`; text = to?.shop ? "Whangamatā Cycles is just ahead" : `Your stop, ${to?.name}, is just ahead`; }
    } else if (d < 30) { key = `${legIndex}-${instr.atM}-now`; text = `${instr.label} now`; }
    else if (d < 90) { key = `${legIndex}-${instr.atM}-soon`; text = `In ${fmtM(d)}, ${instr.label.toLowerCase()}`; }
    else if (d < 260 && !spokenRef.current.startsWith(`${legIndex}-${instr.atM}`)) { key = `${legIndex}-${instr.atM}-far`; text = `In ${fmtM(d)}, ${instr.label.toLowerCase()}`; }
    if (key && spokenRef.current !== key) { spokenRef.current = key; speak(text); }
  }, [instr, offRoute, legIndex, to, arrivedAt, ownerNav, pos, sayCue]);
  useEffect(() => {
    if (offRoute && spokenRef.current !== "off") { spokenRef.current = "off"; speak("You've left the route. Head back toward the blue line."); }
  }, [offRoute]);

  // Leaflet map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: false, attributionControl: false, dragging: true });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    L.polyline(path, { color: NAVY, weight: 5, opacity: 0.4 }).addTo(map);
    const pin = (html) => L.divIcon({ className: "", html, iconSize: [30, 30], iconAnchor: [15, 15] });
    const shopHtml = `<div class="nav-stop-pin" style="width:30px;height:30px;border-radius:8px;background:#0A0A0A;color:#F5F5F0;display:flex;align-items:center;justify-content:center;border:2px solid #F5F5F0;box-shadow:0 2px 8px rgba(0,0,0,0.3);"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 9l1-5h16l1 5M4 9v11h16V9M9 20v-6h6v6"/></svg></div>`;
    markersRef.current.push(L.marker([shopStart.lat, shopStart.lng], { icon: pin(shopHtml), interactive: false }).addTo(map));
    stops.forEach((s, i) => {
      const html = `<div class="nav-stop-pin" style="width:30px;height:30px;border-radius:9999px;background:${NAVY};color:#F5F5F0;display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:13px;font-weight:700;border:2px solid #F5F5F0;box-shadow:0 2px 8px rgba(0,0,0,0.3);">${i + 1}</div>`;
      markersRef.current.push(L.marker([s.lat, s.lng], { icon: pin(html), interactive: false }).addTo(map));
    });
    map.on("dragstart", () => setFollow(false));
    map.setView([from.lat, from.lng], 16);
    return () => {
      try { map.stop(); } catch { /* ignore */ }
      try { map.remove(); } catch { /* ignore */ }
      mapRef.current = null; markersRef.current = []; userRef.current = null; legLineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active leg highlight (bright blue)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (legLineRef.current) { legLineRef.current.remove(); legLineRef.current = null; }
    if (leg.length > 1) legLineRef.current = L.polyline(leg, { color: BLUE, weight: 7, opacity: 0.95 }).addTo(map);
    if (!pos) map.fitBounds(leg.length > 1 ? leg : [[from.lat, from.lng]], { padding: [60, 60], maxZoom: 17 });
  }, [leg, from, pos]);

  // Rider marker, follow + heading-up rotation
  const rotation = headingUp && heading != null ? -heading : 0;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pos) return;
    if (!userRef.current) {
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:44px;height:44px;display:flex;align-items:center;justify-content:center;">
          <div class="nav-dot-pulse" style="position:absolute;width:44px;height:44px;border-radius:9999px;background:rgba(37,99,235,0.22);"></div>
          <div style="width:20px;height:20px;border-radius:9999px;background:#2563EB;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);"></div>
        </div>`,
        iconSize: [44, 44], iconAnchor: [22, 22],
      });
      userRef.current = L.marker(pos, { icon, zIndexOffset: 2000, interactive: false }).addTo(map);
    } else {
      userRef.current.setLatLng(pos);
    }
    const el = userRef.current.getElement();
    if (el) { /* dot marker — no heading rotation needed */ }
    if (offRouteLineRef.current) { offRouteLineRef.current.remove(); offRouteLineRef.current = null; }
    if (offRoute && proj) offRouteLineRef.current = L.polyline([pos, proj.point], { color: "#0A0A0A", weight: 3, dashArray: "6 8" }).addTo(map);
    if (follow) {
      const z = Math.max(map.getZoom(), 17);
      const center = headingUp && heading != null ? destPoint(pos, heading, 70) : pos;
      map.setView(center, z, { animate: true, duration: 0.5 });
    }
  }, [pos, heading, follow, headingUp, rotation, offRoute, proj]);

  useEffect(() => {
    markersRef.current.forEach((m) => {
      const el = m.getElement()?.querySelector(".nav-stop-pin");
      if (el) el.style.transform = `rotate(${-rotation}deg)`;
    });
    if (mapRef.current) mapRef.current.dragging[headingUp && heading != null ? "disable" : "enable"]();
  }, [rotation, headingUp, heading]);

  const [diag, setDiag] = useState(() => Math.ceil(Math.hypot(window.innerWidth, window.innerHeight)));
  useEffect(() => {
    const onR = () => { setDiag(Math.ceil(Math.hypot(window.innerWidth, window.innerHeight))); mapRef.current?.invalidateSize(); };
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const recentre = () => { setFollow(true); if (mapRef.current && pos) mapRef.current.setView(pos, Math.max(mapRef.current.getZoom(), 17), { animate: true }); };
  const backBearing = offRoute && proj && pos ? angleDiff(heading || 0, bearing(pos, proj.point)) : 0;
  const arrivedStop = arrivedAt != null ? points[arrivedAt] : null;
  const toLabel = to?.shop ? "back to Whangamatā Cycles" : to?.name;
  const toSub = to?.shop ? "final leg" : `stop ${legIndex + 1} of ${stops.length}`;

  return (
    <div data-testid="nav-screen" className="fixed inset-0 z-[60] bg-[#0A0A0A] text-white overflow-hidden select-none">
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={containerRef}
          data-testid="nav-map"
          className="absolute"
          style={{ width: diag, height: diag, left: "50%", top: "50%", transform: `translate(-50%, -50%) rotate(${rotation}deg)`, transformOrigin: "50% 50%", transition: "transform 0.6s ease-out" }}
        />
      </div>

      {/* Top: back to stories + instruction banner */}
      <div className="absolute top-0 inset-x-0 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pointer-events-none">
        <div className="pointer-events-auto max-w-xl mx-auto space-y-2">
          <div className="flex items-center justify-between">
            <button data-testid="nav-exit-btn" onClick={onExit} className="h-10 px-4 rounded-full bg-[#F5F5F0] text-[#0A0A0A] text-sm font-medium shadow-lg flex items-center gap-2 active:scale-95 transition-transform">
              <BookOpen className="w-4 h-4" /> Back to stories
            </button>
            <span className="h-10 px-4 rounded-full bg-black/60 backdrop-blur text-[#F5F5F0] text-xs font-mono flex items-center">{route.title}</span>
          </div>
          {offRoute ? (
            <div data-testid="nav-offroute" className="rounded-2xl bg-[#0A0A0A] border border-[#F5F5F0]/20 text-[#F5F5F0] p-4 flex items-center gap-4 shadow-2xl">
              <div className="w-14 h-14 rounded-xl bg-[#2563EB] flex items-center justify-center shrink-0">
                <ArrowUp className="w-9 h-9" strokeWidth={2.5} style={{ transform: `rotate(${backBearing}deg)`, transition: "transform .4s" }} />
              </div>
              <div>
                <p className="font-semibold text-lg leading-tight">You've left the route</p>
                <p className="text-sm text-white/75">Head back toward the blue line · {fmtM(proj.distM)} away</p>
              </div>
            </div>
          ) : finished ? (
            <div data-testid="nav-finished" className="rounded-2xl bg-[#001A4D] text-white p-4 flex items-center gap-4 shadow-2xl">
              <Flag className="w-10 h-10 shrink-0" />
              <div><p className="font-semibold text-lg leading-tight">Ride complete</p><p className="text-sm text-white/75">Welcome back to {SHOP_NAME}</p></div>
            </div>
          ) : instr ? (
            <div data-testid="nav-instruction" className={`rounded-2xl text-white shadow-2xl overflow-hidden ${instr.type === "note" ? (instr.kind === "kids" ? "bg-emerald-800" : "bg-amber-800") : "bg-[#001A4D]"}`}>
              <div className="p-4 flex items-center gap-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center shrink-0 ${instr.type === "note" ? "bg-white/15" : "bg-[#2563EB]"}`}>{instr.type === "note" ? (instr.kind === "kids" ? <HelpCircle className="w-10 h-10" /> : <Sparkles className="w-10 h-10" />) : <TurnIcon type={instr.type} className="w-11 h-11" />}</div>
                <div className="min-w-0">
                  {instr.type === "note" && <p className="uppercase tracking-wider text-[10px] text-white/60">{instr.kind === "kids" ? "Kids question" : "Side quest"}</p>}
                  <p className="font-mono text-3xl font-semibold leading-none" data-testid="nav-instruction-dist">{fmtM(instr.distM)}</p>
                  <p className={`leading-tight mt-1 ${instr.owner ? "text-base line-clamp-3" : "text-lg truncate"}`} data-testid="nav-instruction-text">{instr.label}</p>
                </div>
              </div>
              {instr.then && (
                <div className="px-4 py-2 bg-black/25 text-sm text-white/80 flex items-center gap-2">
                  <span className="uppercase tracking-wider text-[10px] text-white/50">Then</span>
                  {instr.then.type !== "note" && <TurnIcon type={instr.then.type} className="w-4 h-4 shrink-0" />} <span className="truncate">{instr.then.label}</span>
                </div>
              )}
            </div>
          ) : (
            <div data-testid="nav-waiting" className="rounded-2xl bg-[#001A4D] text-white p-4 flex items-center gap-4 shadow-2xl">
              {gpsError ? <AlertTriangle className="w-8 h-8 text-[#93C5FD] shrink-0" /> : <Loader2 className="w-8 h-8 animate-spin shrink-0" />}
              <div>
                <p className="font-semibold leading-tight">{gpsError ? "Location needed" : "Finding your location…"}</p>
                <p className="text-sm text-white/75">{gpsError || (legIndex === 0 ? `Leave Whangamatā Cycles and ride to stop 1: ${to?.name}` : `Ride ${toLabel}`)}</p>
              </div>
            </div>
          )}
          {!ownerNav && legIndex > 0 && from.directions && !offRoute && !finished && arrivedAt == null && (
            <p data-testid="nav-local-cue" className="mx-1 text-sm text-white bg-black/60 backdrop-blur rounded-xl px-3 py-2 leading-snug">
              <span className="text-[#93C5FD] font-medium">Local tip · </span>{from.directions}
            </p>
          )}
        </div>
      </div>

      {/* Right controls */}
      <div className="absolute right-3 top-[54%] -translate-y-1/2 flex flex-col gap-2">
        <button data-testid="nav-mute-btn" onClick={() => setMuted((m) => !m)} className="w-12 h-12 rounded-full bg-[#F5F5F0] text-[#0A0A0A] shadow-lg flex items-center justify-center active:scale-95 transition-transform" aria-label="Toggle voice">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <button data-testid="nav-heading-btn" onClick={() => setHeadingUp((v) => !v)} className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform ${headingUp ? "bg-[#001A4D] text-white" : "bg-[#F5F5F0] text-[#0A0A0A]"}`} aria-label="Toggle heading-up">
          <Compass className="w-5 h-5" style={{ transform: `rotate(${rotation}deg)`, transition: "transform .6s" }} />
        </button>
        <button data-testid="nav-recenter-btn" onClick={recentre} className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform ${follow ? "bg-[#F5F5F0] text-[#0A0A0A]" : "bg-[#2563EB] text-white"}`} aria-label="Recentre">
          <LocateFixed className="w-5 h-5" />
        </button>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 inset-x-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="max-w-xl mx-auto rounded-2xl bg-[#F5F5F0] text-[#0A0A0A] shadow-2xl overflow-hidden">
          {arrivedStop ? (
            <div className="p-4" data-testid="nav-arrival-card">
              <p className="overline text-[#001A4D]">Stop {arrivedAt} of {stops.length} · you've arrived</p>
              <p className="font-serif text-2xl mt-0.5">{arrivedStop.name}</p>
              <p className="text-sm text-black/60 mt-1" data-testid="nav-arrival-hint">
                {countdown != null
                  ? `Rolling on to ${points[arrivedAt + 1]?.shop ? "Whangamatā Cycles" : points[arrivedAt + 1]?.name} in ${countdown}s…`
                  : playing ? "Enjoy the story — we'll carry on automatically when it ends."
                  : "Play the story here. When it finishes (or you ride off), directions continue on their own."}
              </p>
              <div className="flex gap-2 mt-3">
                <button data-testid="nav-play-story-btn" onClick={onPlayStory} disabled={audioLoading} className="flex-1 h-12 rounded-full bg-[#001A4D] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                  {audioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <><Pause className="w-4 h-4" /> Pause story</> : <><Play className="w-4 h-4" /> Play the story</>}
                </button>
                {countdown != null ? (
                  <button data-testid="nav-stay-btn" onClick={() => setCountdown(null)} className="h-12 px-4 rounded-full border border-black/15 text-sm font-medium">Stay here</button>
                ) : (
                  <button data-testid="nav-continue-btn" onClick={continueRide} className="h-12 px-4 rounded-full border border-black/15 text-sm font-medium flex items-center gap-1">
                    Skip <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ) : finished ? (
            <div className="p-4 flex items-center gap-3" data-testid="nav-finished-card">
              <p className="flex-1 font-serif text-xl">Nice riding!</p>
              <button data-testid="nav-finish-btn" onClick={onFinish} className="h-12 px-5 rounded-full bg-[#001A4D] text-white font-medium">See ride summary</button>
            </div>
          ) : (
            <div className="p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-mono text-2xl font-semibold leading-none" data-testid="nav-remaining">
                  {remainingM != null ? fmtM(remainingM) : distToNext != null ? fmtM(distToNext) : "—"}
                  <span className="text-sm font-sans font-normal text-black/50 ml-2">{remainingM != null ? `· ${etaText(remainingM)}` : ""}</span>
                </p>
                <p className="text-sm text-black/60 mt-1 truncate">to <span className="font-medium text-[#0A0A0A]">{toLabel}</span> · {toSub}{speed > 0.5 ? ` · ${Math.round(speed * 3.6)} km/h` : ""}</p>
              </div>
              <button data-testid="nav-sim-btn" onClick={() => setSim((s) => !s)} title="Preview ride" className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${sim ? "bg-[#2563EB] text-white" : "bg-black/5 text-black/50"}`}>
                <FlaskConical className="w-4 h-4" />
              </button>
              <LostButton route={route} stopIndex={Math.max(0, legIndex - 1)} pos={pos} compact />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
